import { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup, Transition } from '@Sage/ssm-web5';
import { Advice, Icon, Loader, Popover, Text, useTheme } from '@Sage/hoi-poi-ui';
import { OPPORTUNITIES, COMPANIES } from 'constants/Entities';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import UtilFormat from 'utils/UtilFormat';

import './styles.scss';

const AddressDisplay = memo(
    ({
        address,
        loadingDistance,
        distance,
        geoPermission,
        coordinates,
        notGeoLocalizedEntityLiteral,
        entity,
        geoAccuracy,
        isFullCollapsible = false,
        preComponent,
    }) => {
        const [active, setActive] = useState(false);

        const theme = useTheme();

        const accuracyPopoverContent = useMemo(() => {
            if (!entity) return null;

            const entityLabel = {
                [OPPORTUNITIES.entity]: 'label_opportunity_singular',
                [COMPANIES.entity]: 'label_account_singular',
            }[entity.entity];

            return (
                <div className="entity-location-geolocation__popoverContent">
                    <Text type="subtitle" medium color={theme.colors.textLight.primary}>
                        {getLiteral('label_geolocation_accuracy')}
                    </Text>
                    <Advice type="semanticInfo" showIcon showCollapse={false}>
                        {getLiteralWithParameters('label_explanation_geolocation_error_margin', [
                            getLiteral(entityLabel),
                        ])}
                    </Advice>
                    <Text
                        className="entity-location-geolocation__popoverContent__footer"
                        color={theme.colors.textLight.secondary}
                    >
                        {`${getLiteral('label_geolocation_accuracy')}: `}
                        {geoAccuracy && geoAccuracy >= 0 ? (
                            <strong>{UtilFormat.getDistanceWithUnit(geoAccuracy)}</strong>
                        ) : (
                            getLiteral('label_not_available')
                        )}
                    </Text>
                </div>
            );
        }, [entity, geoAccuracy, theme.colors.textLight.primary, theme.colors.textLight.secondary]);

        const geolocationDetails = useMemo(() => {
            if (loadingDistance) {
                return <Loader size="small" />;
            }

            let text;

            if (distance) {
                const distanceWithUnit = UtilFormat.getDistanceWithUnit(distance);
                if (distanceWithUnit) {
                    const content = (
                        <div className="entity-location-geolocation__content">
                            {isFullCollapsible && (
                                <Text type="subtitle" medium className="location-widget-address">
                                    {address}
                                </Text>
                            )}
                            <div className="entity-location-geolocation__row">
                                <div className="entity-location-geolocation__col">
                                    <Text color={theme.colors.textLight.secondary}>
                                        {`${getLiteral('label_coordinates')}:`}
                                    </Text>
                                    <Text>
                                        {`${coordinates.lat.toFixed(4)}, ${(coordinates?.lng || coordinates?.lon).toFixed(4)}`}
                                    </Text>
                                </div>
                                <div className="entity-location-geolocation__col">
                                    <Text
                                        color={theme.colors.textLight.secondary}
                                        className="entity-location-geolocation__label"
                                    >
                                        {`${getLiteral('label_distance')}:`}
                                        <Popover
                                            placement="topRight"
                                            content={accuracyPopoverContent}
                                            className="entity-location-geolocation__popover"
                                            trigger={['hover']}
                                        >
                                            <span>
                                                <Icon
                                                    name="info"
                                                    size="small"
                                                    className="entity-location-geolocation__info"
                                                />
                                            </span>
                                        </Popover>
                                    </Text>
                                    <Text>{distanceWithUnit}</Text>
                                </div>
                            </div>
                        </div>
                    );

                    if (isFullCollapsible) {
                        return (
                            <div
                                className={classNames('entity-location-geolocation', {
                                    'entity-location-geolocation--active': active,
                                })}
                            >
                                <div
                                    className="entity-location-geolocation__header"
                                    onClick={() => setActive(!active)}
                                >
                                    <Text color={theme.colors.textLight.jadeAccessible}>
                                        {getLiteral('label_geolocation_details')}
                                    </Text>
                                    <Icon name="arrowDownRaw" size="small" />
                                </div>
                                <TransitionGroup>
                                    <Transition show={active}>
                                        <div
                                            className={classNames(
                                                'entity-location-geolocation__contentWrapper',
                                                {
                                                    'entity-location-geolocation__contentWrapper--hasPreComponent':
                                                        !!preComponent,
                                                },
                                            )}
                                        >
                                            {preComponent}
                                            {content}
                                        </div>
                                    </Transition>
                                </TransitionGroup>
                            </div>
                        );
                    }

                    return content;
                } else {
                    text = getLiteral('placeholder_notgeolocalized');
                }
            } else if (coordinates) {
                // Show coordinates with or without geolocation permission
                const content = (
                    <div className="entity-location-geolocation__content">
                        {isFullCollapsible && (
                            <Text type="subtitle" medium className="location-widget-address">
                                {address}
                            </Text>
                        )}
                        <div className="entity-location-geolocation__row">
                            <div className="entity-location-geolocation__col">
                                <Text color={theme.colors.textLight.secondary}>
                                    {`${getLiteral('label_coordinates')}:`}
                                </Text>
                                <Text>
                                    {`${coordinates.lat.toFixed(4)}, ${(coordinates?.lng || coordinates?.lon).toFixed(4)}`}
                                </Text>
                            </div>
                            {geoPermission !== 'granted' && (
                                <div className="entity-location-geolocation__col">
                                    <Text color={theme.colors.textLight.secondary}>
                                        {`${getLiteral('label_distance')}:`}
                                    </Text>
                                    <Text>
                                        {getLiteral('label_without_geolocation_permission')}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </div>
                );

                if (isFullCollapsible) {
                    return (
                        <div
                            className={classNames('entity-location-geolocation', {
                                'entity-location-geolocation--active': active,
                            })}
                        >
                            <div
                                className="entity-location-geolocation__header"
                                onClick={() => setActive(!active)}
                            >
                                <Text color={theme.colors.textLight.jadeAccessible}>
                                    {getLiteral('label_geolocation_details')}
                                </Text>
                                <Icon name="arrowDownRaw" size="small" />
                            </div>
                            <TransitionGroup>
                                <Transition show={active}>
                                    <div
                                        className={classNames(
                                            'entity-location-geolocation__contentWrapper',
                                            {
                                                'entity-location-geolocation__contentWrapper--hasPreComponent':
                                                    !!preComponent,
                                            },
                                        )}
                                    >
                                        {preComponent}
                                        {content}
                                    </div>
                                </Transition>
                            </TransitionGroup>
                        </div>
                    );
                }

                return content;
            } else if (geoPermission !== 'granted') {
                text = getLiteral('label_no_geolocation_service');
            } else {
                text = getLiteral(notGeoLocalizedEntityLiteral);
            }

            return (
                <Text className="label-not-geolocalized" color={theme.colors.textLight.secondary}>
                    {text}
                </Text>
            );
        }, [
            loadingDistance,
            distance,
            geoPermission,
            theme.colors.textLight.secondary,
            theme.colors.textLight.jadeAccessible,
            active,
            isFullCollapsible,
            address,
            coordinates,
            accuracyPopoverContent,
            notGeoLocalizedEntityLiteral,
            preComponent,
        ]);

        return (
            <div className="entity-location-widget__address">
                {!isFullCollapsible && (
                    <Text type="subtitle" medium className="location-widget-address">
                        {address}
                    </Text>
                )}
                <div className="entity-location-widget__geolocation-details">
                    {geolocationDetails}
                </div>
            </div>
        );
    },
);

AddressDisplay.propTypes = {
    address: PropTypes.string.isRequired,
    loadingDistance: PropTypes.bool,
    distance: PropTypes.number,
    geoPermission: PropTypes.string,
    coordinates: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
    }),
    notGeoLocalizedEntityLiteral: PropTypes.string,
    entity: PropTypes.object.isRequired,
    geoAccuracy: PropTypes.number,
    isFullCollapsible: PropTypes.bool,
    preComponent: PropTypes.node,
};

export default AddressDisplay;
